/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { StudioRole } from './studio';

export enum JoinRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected', // Backend uses 'rejected'
  CANCELLED = 'cancelled', // Note: Backend doesn't support this status - handled via soft delete
}

export interface JoinRequest {
  id: string;
  userId?: string;
  studioId?: string;
  studioName?: string;
  studio_name?: string; // Backend snake_case field
  message: string;
  status: JoinRequestStatus;
  submittedAt?: Date;
  submitted_at: Date; // Backend snake_case field
  reviewedAt?: Date;
  reviewed_at?: Date; // Backend snake_case field
  reviewedBy?: string;
  reviewerName?: string;
  reviewed_by_name?: string; // Backend snake_case field
  denialReason?: string;
  review_notes?: string; // Backend snake_case field (denial reason)
  requestedRole?: StudioRole;
  requested_role?: StudioRole; // Backend snake_case field
  // Backend fields (direct from StudioMembershipRequestSerializer)
  email: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  created_at: Date;
  updated_at: Date;
  // The requester's account; null when the request can't be approved
  // (no linked account, or the account is inactive or unverified)
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string | null;
  } | null;
}

export interface JoinRequestListParams {
  studioId?: string;
  userId?: string;
  search?: string;
  status?: JoinRequestStatus;
  requestedRole?: StudioRole;
  page?: number;
  pageSize?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface SubmitJoinRequestRequest {
  studioId: string;
  studioSlug?: string; // For public endpoint
  message: string;
  requestedRole?: StudioRole;
}

export interface ApproveJoinRequestRequest {
  requestId: string;
  assignedRole: StudioRole;
}

export interface DenyJoinRequestRequest {
  requestId: string;
  reason: string;
  allowResubmission?: boolean;
}

export interface JoinRequestFormData {
  studioId: string;
  message: string;
  requestedRole: StudioRole;
}

export interface StudioSearchResult {
  id: string;
  name: string;
  description: string;
  slug?: string; // URL-friendly name for public endpoints
  logo?: string;
  memberCount?: number; // Frontend camelCase (optional for backward compatibility)
  member_count?: number; // Backend snake_case (what's actually returned)
  isPublic: boolean;
  allowJoinRequests: boolean;
  hasExistingRequest?: boolean;
  isMember?: boolean;
}

export interface StudioSearchParams {
  search?: string;
  isPublic?: boolean;
  allowJoinRequests?: boolean;
  minMembers?: number;
  maxMembers?: number;
  page?: number;
  pageSize?: number;
}

export interface JoinRequestMetrics {
  total: number;
  pending: number;
  approved: number;
  denied: number;
  cancelled: number;
  approvalRate: number;
  averageResponseTime: number; // in hours
}

// Component prop interfaces
export interface JoinRequestFormProps {
  onSuccess?: (request: JoinRequest) => void;
  onCancel?: () => void;
  open?: boolean;
  preselectedStudioId?: string;
}

export interface StudioSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onStudioSelect: (studio: StudioSearchResult) => void;
  excludeStudioIds?: string[];
}

export interface JoinRequestListProps {
  studioId: string;
  searchParams?: JoinRequestListParams;
  onRequestUpdate?: (request: JoinRequest) => void;
}

export interface JoinRequestCardProps {
  request: JoinRequest;
  onApprove?: (requestId: string, role: StudioRole) => void;
  onDeny?: (requestId: string, reason: string) => void;
  onView?: (requestId: string) => void;
  showActions?: boolean;
}

export interface RequestStatusBadgeProps {
  status: JoinRequestStatus;
  submittedAt?: Date;
  reviewedAt?: Date;
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
}

export interface MyJoinRequestsProps {
  userId?: string;
  onCancel?: (requestId: string) => void;
  onResubmit?: (studioId: string) => void;
  onRequestToJoin?: () => void;
}

export interface JoinRequestApprovalDialogProps {
  open: boolean;
  request: JoinRequest | null;
  onClose: () => void;
  onApprove: (
    requestId: string,
    role: StudioRole,
    welcomeMessage?: string
  ) => void;
}

export interface JoinRequestDenialDialogProps {
  open: boolean;
  request: JoinRequest | null;
  onClose: () => void;
  onDeny: (
    requestId: string,
    reason: string,
    allowResubmission?: boolean
  ) => void;
}

// Predefined denial reasons
export const DENIAL_REASONS = [
  'Studio is currently at capacity',
  'Requirements not met',
  'Duplicate request',
  'Studio is private/invite-only',
  'Insufficient information provided',
  'Other (please specify)',
] as const;

export type DenialReason = (typeof DENIAL_REASONS)[number];
